'use client';

import { updatePreviewIframe } from '@/components/live-preview/update-preview-iframe';
import { useIframeLoadTracker } from '@/components/live-preview/use-iframe-load-tracker';
import { usePreviewToolbarPortal } from '@/components/live-preview/use-preview-toolbar-portal';
import { AB_PREVIEW_QUERY_PARAM } from '@/utils/ab-preview';
import { getQueryParam } from '@/utils/url';
import { useField, useFormFields } from '@payloadcms/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { FormState, TextFieldClientComponent } from 'payload';

import './styles.scss';

interface PageOption {
  id: string;
  path: string | null;
  label: string;
  type: 'control' | 'variant';
  probability?: number;
}

// Paths come from virtual fields populated by the afterRead hook in ABTests.ts.
// Returns a JSON string so the useFormFields selector produces a value-stable
// result (strings compare by value, arrays don't).
const serializeVariants = (fields: FormState): string => {
  const variants: Array<{ id: string; path: string | null; probability: number }> = [];

  for (let i = 0; fields[`variants.${i}.target`]; i++) {
    const id = fields[`variants.${i}.target`].value as string | null;
    const path = (fields[`variants.${i}._targetPath`]?.value as string) || null;
    const probability = (fields[`variants.${i}.probability`]?.value as number) || 0;

    if (id) {
      variants.push({ id, path, probability });
    }
  }

  return JSON.stringify(variants);
};

const setQueryParam = (value: string | null) => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (value) {
    url.searchParams.set(AB_PREVIEW_QUERY_PARAM, value);
  } else {
    url.searchParams.delete(AB_PREVIEW_QUERY_PARAM);
  }
  window.history.replaceState({}, '', url.toString());
};

const PreviewPageSelector: TextFieldClientComponent = ({ path }) => {
  const { setValue } = useField<string>({ path });
  const { isLoading, startLoading } = useIframeLoadTracker();
  const portalTarget = usePreviewToolbarPortal('fv-ab-preview-selector-wrapper');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const initializedRef = useRef(false);

  const controlId = useFormFields(([fields]) => (fields.control?.value as string) || null);
  const controlPath = useFormFields(([fields]) => (fields._controlPath?.value as string) || null);
  const variantsJson = useFormFields(([fields]) => serializeVariants(fields));

  const variants = useMemo(
    () => JSON.parse(variantsJson) as Array<{ id: string; path: string | null; probability: number }>,
    [variantsJson],
  );

  const pageOptions = useMemo(() => {
    const options: PageOption[] = [];

    if (controlId) {
      options.push({
        id: controlId,
        path: controlPath,
        label: controlPath ? `Control: ${controlPath}` : 'Control Page',
        type: 'control',
      });
    }

    variants.forEach((variant, index) => {
      options.push({
        id: variant.id,
        path: variant.path,
        label: variant.path
          ? `Variant ${index + 1}: ${variant.path} (${Math.round(variant.probability * 100)}%)`
          : `Variant ${index + 1} (${Math.round(variant.probability * 100)}%)`,
        type: 'variant',
        probability: variant.probability,
      });
    });

    return options;
  }, [controlId, controlPath, variants]);

  // The iframe is removed and re-inserted to reset its browsing context,
  // preventing back-button pollution when toggling between pages.
  const applyPreviewIframe = useCallback((pageId: string, pagePath: string | null) => {
    const iframe = document.querySelector('.live-preview-window iframe') as HTMLIFrameElement | null;
    if (!iframe?.src) return;

    try {
      const url = new URL(iframe.src);
      if (pagePath) {
        url.pathname = pagePath;
      }
      url.searchParams.set(AB_PREVIEW_QUERY_PARAM, pageId);
      updatePreviewIframe(url.toString());
    } catch {
      // iframe re-insertion may fail if the DOM was modified externally
    }
  }, []);

  useEffect(() => {
    if (initializedRef.current || !pageOptions.length) return;

    const queryParamValue = getQueryParam(AB_PREVIEW_QUERY_PARAM);
    const validIds = new Set(pageOptions.map((p) => p.id));

    const initialPageId = queryParamValue && validIds.has(queryParamValue) ? queryParamValue : controlId;

    if (initialPageId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedPageId(initialPageId);
      setValue(initialPageId, true);
      setQueryParam(initialPageId);
      initializedRef.current = true;

      if (initialPageId !== controlId) {
        const page = pageOptions.find((p) => p.id === initialPageId);
        startLoading();
        applyPreviewIframe(initialPageId, page?.path ?? null);
      }
    }
  }, [pageOptions, controlId, setValue, applyPreviewIframe, startLoading]);

  const handlePageSelect = useCallback(
    (pageId: string) => {
      if (pageId === selectedPageId) {
        setIsOpen(false);
        return;
      }

      const page = pageOptions.find((p) => p.id === pageId);

      startLoading();
      setSelectedPageId(pageId);
      setIsOpen(false);

      setValue(pageId, true);

      // livePreview.url reads this from the referer header as a fallback
      setQueryParam(pageId);

      applyPreviewIframe(pageId, page?.path ?? null);
    },
    [selectedPageId, pageOptions, setValue, applyPreviewIframe, startLoading],
  );

  const selectedPageData = pageOptions.find((p) => p.id === selectedPageId);

  if (!pageOptions.length) {
    return null;
  }

  const selectorUI = (
    <div className="fv-ab-preview-selector">
      <button
        type="button"
        className={['fv-ab-preview-selector__trigger', isLoading && 'fv-ab-preview-selector__trigger--loading'].filter(Boolean).join(' ')}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="fv-ab-preview-selector__label">
          {isLoading ? (
            <span className="fv-ab-preview-selector__loading-wrapper">
              <span className="fv-ab-preview-selector__spinner" />
              Loading...
            </span>
          ) : (
            <>
              <span className={`fv-ab-preview-selector__dot fv-ab-preview-selector__dot--${selectedPageData?.type ?? 'control'}`} />
              <span className="fv-ab-preview-selector__label-text">{selectedPageData?.label || 'Select page'}</span>
            </>
          )}
        </span>
        <span className="fv-ab-preview-selector__chevron">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <>
          <div className="fv-ab-preview-selector__backdrop" onClick={() => setIsOpen(false)} />
          <div className="fv-ab-preview-selector__dropdown">
            {pageOptions.map((page) => (
              <button
                key={page.id}
                type="button"
                className={['fv-ab-preview-selector__option', page.id === selectedPageId && 'fv-ab-preview-selector__option--selected']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => handlePageSelect(page.id)}
              >
                <span className={`fv-ab-preview-selector__dot fv-ab-preview-selector__dot--${page.type}`} />
                {page.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  if (portalTarget) {
    return createPortal(selectorUI, portalTarget);
  }

  return null;
};

export default PreviewPageSelector;
